import { useEffect, useRef, useState } from "react";
import { getConversationIntelMap } from "../../../api/conversation";

// The conversation-list intelligence enrichment, shared by the admin and client
// detail pages.
//
// The list itself loads from the `conversations` table, which carries none of
// the derived fields the row content, filters and export columns are specified
// against — outcome, stay_type_sought, booking_urgency, conversion_barriers,
// estimated_lost_value, requires_followup. Those live only on
// fn_get_conversation_list, so they are fetched separately and merged onto the
// rows by conversation id. Enrichment is progressive: a row the RPC does not
// return simply keeps its existing render, and a failed fetch costs the page
// nothing but the extra fields.
//
// It runs on voice and chat only. Any other modality has no analysis band, no
// intelligence filters and no enriched export columns, so the fetch would be
// several hundred rows nobody reads.
//
// STALENESS IS DECIDED BY THE KEY, NOT BY A CLEANUP FLAG. This effect depends on
// values that change while its own request is in flight — the loaded row count
// grows as the list pages in, and the date range settles after mount — so a
// cleanup-set `cancelled` flag was reliably true by the time the response
// arrived. The result was dropped, and because the key had already been claimed
// the effect never asked again: every row rendered with no outcome, no stay type
// and no barriers while a successful 200 sat in the network panel. Comparing the
// key on arrival keeps a superseded response out without discarding the one that
// is still current.
//
// THE REQUEST IS SCOPED TO THE VISIBLE RANGE. Unscoped, the RPC returns page one
// of every conversation the property has ever had, and the handful of pages the
// helper fetches need not overlap the days on screen — on chat, where roughly
// 39,000 conversations are still draining through a backfill, they did not. The
// list only ever shows rows inside the range, so narrowing the query loses
// nothing and costs less.

const ENRICHED_MODALITIES = new Set(["voice", "chat"]);

export const enrichmentKey = (clientId, modality, startDate, endDate) =>
  `${clientId}:${modality}:${startDate}:${endDate}`;

/**
 * @param {object} args
 * @param {string|null} args.clientId
 * @param {string|null} args.modality  "voice" | "chat" | anything else (no-op)
 * @param {string|null} args.startDate
 * @param {string|null} args.endDate
 * @param {boolean} args.ready  false while the list is still loading or empty —
 *   the rows the map decorates have to exist first, and the page-load burst has
 *   to drain before this adds to it.
 * @returns {object} conversation_id → intelligence record. `{}` until the first
 *   result lands, and `{}` again on failure.
 */
export const useIntelEnrichment = ({ clientId, modality, startDate, endDate, ready }) => {
  const [intelMap, setIntelMap] = useState({});
  const appliedFor = useRef(null);

  useEffect(() => {
    if (!clientId || !ENRICHED_MODALITIES.has(modality)) {
      appliedFor.current = null;
      setIntelMap({});
      return;
    }
    const key = enrichmentKey(clientId, modality, startDate, endDate);
    // Drop a map fetched for a DIFFERENT key before deciding whether to wait.
    // This ran after the `!ready` return, so changing the date range left the
    // previous range's records merged onto the rows while the list reloaded —
    // and in the overlap between two ranges the guest default was filtering on
    // records fetched for the other one. Records that no longer describe what is
    // on screen are worse than none.
    if (appliedFor.current !== null && appliedFor.current !== key) {
      appliedFor.current = null;
      setIntelMap({});
    }
    if (!ready) return;
    if (appliedFor.current === key) return;
    appliedFor.current = key;
    getConversationIntelMap(clientId, { modality, startDate, endDate })
      .then((m) => { if (appliedFor.current === key) setIntelMap(m); })
      .catch(() => { if (appliedFor.current === key) setIntelMap({}); });
  }, [clientId, modality, startDate, endDate, ready]);

  return intelMap;
};

export default useIntelEnrichment;
